import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit, Download, Trash2, X, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { API, statusColors, formatDate } from "./utils";
import { useFormDraft } from "@/hooks/useFormDraft";
import { DraftRestoredBanner } from "@/components/DraftRestoredBanner";
import { DraftIndicatorBadge } from "@/components/DraftIndicatorBadge";

export function VariationsContent({ projectId, canWrite, token }) {
  const [variations, setVariations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVariation, setEditingVariation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const defaultItem = { scope_of_work: "", unit: "", quantity: 1, brillen_price: 0, vendor_price: 0 };
  const variationFormDefaults = {
    title: "Variations Document",
    items: [{ ...defaultItem }],
    notes: ""
  };
  const [formData, setFormData, draft] = useFormDraft(
    `newVariation:${projectId}`,
    variationFormDefaults,
    { enabled: showForm && !editingVariation }
  );
  const [draftRestored, setDraftRestored] = useState(false);

  useEffect(() => { fetchVariations(); }, [projectId]);

  const fetchVariations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/variations/project/${projectId}`, { headers: { Authorization: `Bearer ${token}` } });
      setVariations(res.data);
    } catch (error) { console.error("Failed to fetch variations", error); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData(variationFormDefaults);
    setEditingVariation(null);
    setShowForm(false);
    setDraftRestored(false);
  };

  const openNewVariationForm = () => {
    const restored = draft.restoreIfAny();
    setDraftRestored(restored);
    setShowForm(true);
  };

  const handleEditVariation = async (variation) => {
    try {
      const res = await axios.get(`${API}/variations/${variation.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setFormData({
        title: res.data.title,
        items: res.data.items.map(i => ({ scope_of_work: i.scope_of_work, unit: i.unit, quantity: i.quantity, brillen_price: i.brillen_price, vendor_price: i.vendor_price })),
        notes: res.data.notes || ""
      });
      setEditingVariation(variation);
      setShowForm(true);
    } catch (error) { toast.error("Failed to load variation"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0 || !formData.items[0].scope_of_work) {
      toast.error("Please add at least one item");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { project_id: projectId, ...formData };
      if (editingVariation) {
        await axios.put(`${API}/variations/${editingVariation.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Variation updated!");
      } else {
        await axios.post(`${API}/variations`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Variation created!");
      }
      resetForm();
      fetchVariations();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save variation"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this variation document?")) return;
    try {
      await axios.delete(`${API}/variations/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Variation deleted");
      fetchVariations();
    } catch (error) { toast.error("Failed to delete"); }
  };

  const handleDownloadPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/variations/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Variation_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded");
    } catch (error) { toast.error("Failed to download PDF"); }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: field === 'quantity' || field === 'brillen_price' || field === 'vendor_price' ? parseFloat(value) || 0 : value };
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => setFormData({ ...formData, items: [...formData.items, { ...defaultItem }] });
  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
    }
  };

  const calculateTotal = () => formData.items.reduce((sum, item) => sum + ((item.brillen_price - item.vendor_price) * item.quantity), 0);

  if (loading) return <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brillien-600" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-slate-900">Variations Documents</h4>
        {canWrite && !showForm && (
          <div className="flex items-center gap-3">
            <DraftIndicatorBadge
              storageKey={`newVariation:${projectId}`}
              onResume={openNewVariationForm}
            />
            <Button onClick={openNewVariationForm} className="bg-brillien-600 hover:bg-brillien-700" data-testid="create-variation-btn">
              <Plus className="h-4 w-4 mr-2" />New Variation
            </Button>
          </div>
        )}
      </div>

      {showForm && (
        <Card className="p-5 border-brillien-200 bg-brillien-50/50">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold">{editingVariation ? `Edit - ${editingVariation.id}` : "New Variations Document"}</h4>
            <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingVariation && draftRestored && (
              <DraftRestoredBanner
                onDiscard={() => {
                  draft.clear();
                  setDraftRestored(false);
                }}
              />
            )}
            <div><Label>Title *</Label><Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required /></div>
            
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-100">
                    <TableHead className="w-10">S.No</TableHead>
                    <TableHead className="min-w-[200px]">Scope of Work</TableHead>
                    <TableHead className="w-20">Unit</TableHead>
                    <TableHead className="w-20">Qty</TableHead>
                    <TableHead className="w-28">Brillen Price</TableHead>
                    <TableHead className="w-28">Vendor Price</TableHead>
                    <TableHead className="w-28">Variation</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-center font-medium">{idx + 1}</TableCell>
                      <TableCell><Input value={item.scope_of_work} onChange={e => handleItemChange(idx, "scope_of_work", e.target.value)} placeholder="Scope of work" className="border-slate-300" /></TableCell>
                      <TableCell><Input value={item.unit} onChange={e => handleItemChange(idx, "unit", e.target.value)} placeholder="sqft" className="border-slate-300 text-center" /></TableCell>
                      <TableCell><Input type="number" value={item.quantity} onChange={e => handleItemChange(idx, "quantity", e.target.value)} className="border-slate-300 text-center" /></TableCell>
                      <TableCell><Input type="number" value={item.brillen_price} onChange={e => handleItemChange(idx, "brillen_price", e.target.value)} className="border-slate-300 text-right" /></TableCell>
                      <TableCell><Input type="number" value={item.vendor_price} onChange={e => handleItemChange(idx, "vendor_price", e.target.value)} className="border-slate-300 text-right" /></TableCell>
                      <TableCell className="text-right font-medium">Rs. {((item.brillen_price - item.vendor_price) * item.quantity).toLocaleString()}</TableCell>
                      <TableCell><Button type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)} disabled={formData.items.length === 1}><Trash2 className="h-4 w-4 text-red-500" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-between items-center">
              <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="h-4 w-4 mr-1" />Add Item</Button>
              <div className="text-right"><span className="text-slate-600">Total Variation: </span><span className="font-bold text-lg">Rs. {calculateTotal().toLocaleString()}</span></div>
            </div>
            <div><Label>Notes</Label><Textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} placeholder="Additional notes..." /></div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">{submitting ? "Saving..." : editingVariation ? "Update" : "Create"}</Button>
              <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {variations.length === 0 && !showForm ? (
        <div className="text-center py-12 text-slate-500"><ClipboardList className="h-12 w-12 mx-auto mb-3 text-slate-300" /><p>No variation documents yet</p></div>
      ) : !showForm && (
        <div className="space-y-3">
          {variations.map(v => (
            <Card key={v.id} className="p-4 border-slate-200">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2"><span className="font-mono text-sm text-brillien-600">{v.id}</span><Badge className={v.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>{v.status}</Badge></div>
                  <h4 className="font-semibold mt-1">{v.title}</h4>
                  <p className="text-sm text-slate-600">{v.items?.length || 0} items</p>
                  <p className="text-lg font-bold text-brillien-600 mt-1">Total: Rs. {(v.total_variation || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-500 mt-1">Created: {formatDate(v.created_at)} | Updated: {formatDate(v.updated_at)}</p>
                </div>
                <div className="flex gap-2">
                  {canWrite && v.status === "draft" && <Button variant="outline" size="sm" onClick={() => handleEditVariation(v)} className="text-blue-600"><Edit className="h-4 w-4 mr-1" />Edit</Button>}
                  <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(v.id)} className="text-brillien-600"><Download className="h-4 w-4 mr-1" />PDF</Button>
                  {canWrite && <Button variant="ghost" size="sm" onClick={() => handleDelete(v.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
